import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { FaChevronLeft } from 'react-icons/fa'
import { useNavigate } from 'react-router-dom'

import { Avatar, Box, Button, Grid } from '@mui/material'
import { Edit } from '@mui/icons-material'



import { Form, Formik } from 'formik'
import ChatIcon from '@mui/icons-material/Chat';

import SubmitButton from 'components/atoms/buttons/SubmitButton'
import DatePickerField from 'components/atoms/fields/DatePickerField'
import InputField from 'components/atoms/fields/InputField'
import SelectField from 'components/atoms/fields/SelectField'

import formModel, { initialValues, validationSchema } from 'utils/formModels/studentModel'
import { crudColorMap, crudIconMap, emergencyContactTypeSelectData, genderSelectData, gradeSelectData } from 'utils/helpers'

import { ActionType, IStudent } from 'types'
import RichTextField from 'components/atoms/fields/RichTextField'
import { PRIMARY_TRANSPARENT_GREEN } from 'utils/customizedTheme'
import { useGET_groupsQuery } from 'redux/api/groupsApi'
import SectionTitle from 'components/atoms/SectionTitle'
import { useGET_usersSelectQuery } from 'redux/api/usersApi'

interface IProps {
  action: ActionType
  entity: any
  viewOnly: boolean
  tabKey?: string
  tabText?: string
  handleSubmit: any
  isLoading: boolean
}

const StudentForm: FC<IProps> = ({ action, entity, viewOnly, handleSubmit, isLoading }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const id = entity?.id

  const backToSlug ='/students'
  const editSlug =`/students/edit/${id}`

  const {
    parents,
    email,
    first_name,
    mid_name,
    last_name,
    gender,
    birthday,
    phone_number,
    mobile_number,
    address,
    city,
    district,
    state,
    zip,
    school_academic_id,
    academic_id,
    grade_level,
    comment,
    emergency_contact_name,
    emergency_contact_relationship,
    emergency_contact_phone,
    emergency_contact_email,
    emergency_contact_address,
    emergency_contact_city,
    emergency_contact_district,
    emergency_contact_state,
    emergency_contact_zip,
  } = formModel()
  
  const serverValues = entity?.id
    ? {
        ...initialValues,
        ...entity,
        gender: genderSelectData().data.list.find((r) => r.id === entity?.gender),
        grade_level: gradeSelectData().data.list.find((g) => g.id === entity?.grade_level),
      }
    : initialValues

  const { data: groups } = useGET_groupsQuery({page: 1, page_size: 999, student: entity?.student_profile?.id}, {skip: !entity})  ;
 

  return (
    <>

    { serverValues &&  serverValues?.id &&
      <Box className="thick-border" sx={{ display: 'inline-block', background: '#fff', marginBottom: '40px', width: 'auto', maxWidth: '800px' }}>
        <div style={{ display: 'flex', alignItems: 'start' }}>

          {
            serverValues?.avatar &&

            <div style={{ width: '240px', paddingRight: '40px' }}>
              <Avatar
                        sx={{
                          bgcolor: PRIMARY_TRANSPARENT_GREEN,
                          width: 200,
                          height: 200,
                          display: 'inline-block',
                        }}
                        variant='circular'
                        src={serverValues?.avatar}
                      >
              </Avatar>
            </div>
          }
          
          <div style={{ width: '300px' }}>
              <div>{t('common.name')}: <b>{ serverValues.first_name } { serverValues.mid_name } { serverValues.last_name }</b></div>
              <div style={{ marginTop: '10px' }}>{t('common.academic-id')}: <b>{ serverValues.academic_id }</b></div>
              <div style={{ marginTop: '10px' }}>{t('common.email')}: <b>{ serverValues.email }</b></div>
              <div style={{ marginTop: '10px' }}>{t('common.phone')}: <b>{ serverValues.phone_number }</b></div>
              { serverValues.grade_level && <div style={{ marginTop: '10px' }}>{t('common.grade-level')}: <b>{ serverValues.grade_level.title }{t('common.rd')} {t('common.grade')}</b></div>}
              { groups && groups.results && groups.results.length>0 && <div style={{ marginTop: '10px' }}>{t('common.class')}: <b>{ [groups.results[0]].map(g => g.grade_level+'. '+g.sign).join(', ') }</b></div>  } 
              <div style={{ marginTop: '10px' }}><b>{ serverValues.state }, { serverValues.city }, { serverValues.zip }</b></div>   
              <div style={{ marginTop: '10px' }}><b>{ serverValues.address }</b></div>     
          </div>

          <div style={{ paddingLeft: '20px', alignSelf: 'start' }}>
              <div>{serverValues.parents && <div>{t('common.parents')}: <b>{ serverValues.parents.map(p => p.title ).join(', ') }</b></div>}</div>

              <div style={{ marginTop: '10px' }}>{t('common.emergency-contact')}: <b>{ serverValues.emergency_contact_name }</b></div>   
              <div style={{ marginTop: '10px' }}>{t('common.email')}: <b>{ serverValues.emergency_contact_email }</b></div>
              <div style={{ marginTop: '10px' }}>{t('common.phone')}: <b>{ serverValues.emergency_contact_phone }</b></div>
              <div style={{ marginTop: '10px' }}><b>{ serverValues.emergency_contact_state }, 
                { serverValues.emergency_contact_city }, { serverValues.emergency_contact_zip }</b></div>    
              <div style={{ marginTop: '10px' }}><b>{ serverValues.emergency_contact_address }</b></div>    

             
          </div>


        </div> 
      </Box>
    }


    <Formik
      enableReinitialize
      initialValues={serverValues}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        const data: IStudent = {
          ...values,
          gender: values?.gender?.id ?? '',
          student_profile: {
            academic_id: values.academic_id,
            grade_level: values?.grade_level?.id ?? '',
            comment: values.comment,
            parents_ids: values.parents.map((parent) => parent.id),
          },
        }

        handleSubmit(action, id, data)
      }}
    >
      <Form>
        <Grid container spacing={4} sx={{ marginTop: '-1rem' }}>
          <Grid item xs={12} md={4}>
            <InputField fieldProps={{ ...academic_id }} disabled={viewOnly} />
          </Grid>

          <Grid item xs={12} md={4}>
            <SelectField
              renderChildren={false}
              fieldProps={{ ...grade_level }}
              apiCall={gradeSelectData}
              disabled={viewOnly}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <SelectField
              multiple={true}
              fieldProps={{ ...parents }}
              apiCall={useGET_usersSelectQuery}
              disabled={viewOnly}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <InputField fieldProps={{ ...first_name }} disabled={viewOnly} />
          </Grid>
          <Grid item xs={12} md={4}>
            <InputField fieldProps={{ ...mid_name }} disabled={viewOnly} />
          </Grid>
          <Grid item xs={12} md={4}>
            <InputField fieldProps={{ ...last_name }} disabled={viewOnly} />
          </Grid>

          <Grid item xs={12} md={4} lg={2}>
            <DatePickerField fieldProps={{ ...birthday }} disabled={viewOnly} maxDate={new Date()} />
          </Grid>
          <Grid item xs={12} md={4} lg={2}>
            <SelectField
              renderChildren={false}
              fieldProps={{ ...gender }}
              apiCall={genderSelectData}
              disabled={viewOnly}
            />
          </Grid>
          <Grid item xs={12} md={4} lg={4}>
            <InputField fieldProps={{ ...email }} disabled={viewOnly} />
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <InputField fieldProps={{ ...phone_number }} disabled={viewOnly} />
          </Grid>


          <Grid item xs={12} md={12}>
             <SectionTitle text={`${t('common.home-address')}`} />
          </Grid>  

          <Grid item xs={12}>
            <InputField fieldProps={{ ...address }} disabled={viewOnly} />
          </Grid>

          <Grid item xs={12} md={6} lg={3}>
            <InputField fieldProps={{ ...city }} disabled={viewOnly} />
          </Grid>

          <Grid item xs={12} md={6} lg={3}>
            <InputField fieldProps={{ ...district }} disabled={viewOnly} />
          </Grid>

          <Grid item xs={12} md={6} lg={3}>
            <InputField fieldProps={{ ...state }} disabled={viewOnly} />
          </Grid>

          <Grid item xs={12} md={6} lg={3}>
            <InputField fieldProps={{ ...zip }} disabled={viewOnly} />
          </Grid>

          <Grid item xs={12} md={12}>
             <SectionTitle text={`${t('common.emergency-contact')}`} />
          </Grid>  

          <Grid item xs={12} md={6}>
            <InputField fieldProps={{ ...emergency_contact_name }} disabled={viewOnly} />
          </Grid>

          <Grid item xs={12} md={6}>
            <InputField fieldProps={{ ...emergency_contact_address }} disabled={viewOnly} />
          </Grid>

          <Grid item xs={12} md={6}>
            <InputField fieldProps={{ ...emergency_contact_email }} disabled={viewOnly} />
          </Grid>
          <Grid item xs={12} md={6}>
            <InputField fieldProps={{ ...emergency_contact_phone }} disabled={viewOnly} />
          </Grid>

          <Grid item xs={12} md={6} lg={3}>
            <InputField fieldProps={{ ...emergency_contact_city }} disabled={viewOnly} />
          </Grid>

          <Grid item xs={12} md={6} lg={3}>
            <InputField fieldProps={{ ...emergency_contact_district }} disabled={viewOnly} />
          </Grid>

          <Grid item xs={12} md={6} lg={3}>
            <InputField fieldProps={{ ...emergency_contact_state }} disabled={viewOnly} />
          </Grid>

          <Grid item xs={12} md={6} lg={3}>
            <InputField fieldProps={{ ...emergency_contact_zip }} disabled={viewOnly} />
          </Grid>



          <Grid item xs={12} >
            <RichTextField fieldProps={{ ...comment }} label={`${t('common.comment')}`} icon={<ChatIcon/>} disabled={viewOnly} />
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex' }} className="transparent-background">
              <Button
                variant='outlined'
                color='black'
                onClick={() => navigate(backToSlug)}
                sx={{ minHeight: '2.5rem', width: '10rem' }}
              >
                <FaChevronLeft style={{ fontSize: '1.1rem', margin: '-3px 0.5rem 0 -0.5rem' }} />
                {`${t('common.back')}`}
              </Button>
              {action === 'view' && 
                <Box sx={{ marginLeft: 'auto' }}>
                  <Button
                    variant='outlined'
                    color='black'
                    onClick={() => navigate(editSlug)}
                    sx={{ minHeight: '2.5rem', width: '10rem' }}
                  >
                    <Edit style={{ fontSize: '1.1rem', margin: '-3px 0.5rem 0 -0.5rem' }} />
                    {`${t('common.edit')}`}
                  </Button>
                </Box>
              }
              {action !== 'view' && (
                <SubmitButton
                  color={crudColorMap[action]}
                  text={`${t('form-actions.' + action)}`}
                  icon={crudIconMap[action]}
                  sx={{ width: '10rem' }}
                  isLoading={isLoading}
                />
              )}
            </Box>
          </Grid>
        </Grid>
      </Form>
    </Formik>
   </>
  )
}
export default StudentForm
